/**
 * Brazil pilot partner desk (W2-DESK-CORE, D365, ADR-0047) — country-pack data.
 *
 * Owner decisions D363 #2/#3/#4: the "PDV" is a **partner desk** (a hotel, an inn, a company ordering for a guest),
 * a desk order is paid either by the guest in cash or billed to the partner account, and in the first cycle the desk
 * sells **a car or a motorcycle**. This module is the Brazil realization of the two neutral, governed policies that
 * decide those things per market:
 *
 *  - `payment.partnerBookingMethods` (`@vello/config`) — the payer set of a partner booking. Both pilot markets
 *    publish `[cash, partner_account]` as version 1. This does **not** touch ADR-0046's cash-only
 *    `payment.allowedMethods` rows: a rider still has cash only, and a guest-cash desk order is plain `cash` under
 *    exactly those rows. Billing an account is a different payer model, so it is a different, narrow-only key.
 *  - `partner.deskRideCategories` (`@vello/business`) — which categories the desk may sell. Both pilot markets
 *    publish `[go, moto]` (car and motorcycle) as version 1, and that list is what turns
 *    `category.capabilities.thirdPartyRide` on for the desk: a city closes the desk, or changes what it sells, by
 *    publishing a new version — no redeploy.
 *
 * Both are published through the same neutral seed boundary as the rest of the pack (`@vello/pack-seed`
 * `seedCountryPacks`), so the pilot seed picks them up and the universal API only ever reads the store. A re-run is a
 * no-op (a published `(key, scope, scopeRef, version)` is immutable); ending or widening the arrangement is a new,
 * higher version, never an edit.
 *
 * This module imports nothing from `./brazil.js` (which imports it), so the market ids are spelled here as data.
 */
import { partnerBookingPaymentMethodsSetting, marketId } from '@vello/config';
import { deskRideCategoriesSetting } from '@vello/business';
/** The pilot markets whose partner desks are open (D353 #4). */
export const BRAZIL_PARTNER_DESK_PILOT_MARKETS = [marketId('br:paracatu'), marketId('br:patos-de-minas')];
/** The instant the pilot partner-desk policies take effect (the D363 owner-decision date, UTC). */
export const BRAZIL_PARTNER_DESK_EFFECTIVE_FROM = '2026-09-17T00:00:00Z';
/** The payers a pilot desk may offer: the guest pays the driver in cash, or the partner account is billed (D363 #3). */
export const BRAZIL_PARTNER_DESK_PAYERS = ['cash', 'partner_account'];
/** What a pilot desk sells in the first cycle: a car (`go`) or a motorcycle (`moto`) (D363 #4). */
export const BRAZIL_PARTNER_DESK_CATEGORY_CODES = ['go', 'moto'];
/** The market-scoped partner-booking payer rows for the pilot markets (version 1 each). */
export function brazilPartnerDeskPayerSettings() {
    return BRAZIL_PARTNER_DESK_PILOT_MARKETS.map((market) => partnerBookingPaymentMethodsSetting({
        scope: 'market',
        scopeRef: market,
        methods: BRAZIL_PARTNER_DESK_PAYERS,
        version: 1,
        effectiveFrom: BRAZIL_PARTNER_DESK_EFFECTIVE_FROM,
    }));
}
/** The market-scoped desk category rows for the pilot markets (version 1 each). */
export function brazilPartnerDeskCategorySettings() {
    return BRAZIL_PARTNER_DESK_PILOT_MARKETS.map((market) => deskRideCategoriesSetting({
        scope: 'market',
        scopeRef: market,
        categoryCodes: BRAZIL_PARTNER_DESK_CATEGORY_CODES,
        version: 1,
        effectiveFrom: BRAZIL_PARTNER_DESK_EFFECTIVE_FROM,
    }));
}
/** Every partner-desk policy row the Brazil pack publishes. */
export function brazilPartnerDeskSettings() {
    return [...brazilPartnerDeskPayerSettings(), ...brazilPartnerDeskCategorySettings()];
}
//# sourceMappingURL=partner-desk.js.map